import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MapPin, Mail, Clock, Send, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import { COMPANY } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.length < 2 || !form.email || form.message.length < 5) {
      toast.error("Please provide your name, email and message.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message sent — we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send message. Please call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact EL-Gas and Plumbing | Lower Clapton, Hackney & Islington"
        description="Phone, email or message Ali and the EL-Gas team. Gas Safe engineers based on Median Rd, Lower Clapton, serving Hackney & Islington."
        path="/contact"
      />
      <section className="bg-brand-navy text-white py-20" data-testid="contact-hero">
        <div className="container-x">
          <span className="overline text-brand-orange">Contact us</span>
          <h1 className="mt-3 font-heading font-black text-5xl md:text-6xl tracking-tighter">
            Speak to a Gas Safe engineer.
          </h1>
          <p className="mt-4 text-slate-300 max-w-2xl">
            Phone, email or drop into our Clapton office on Median Rd. Emergency line answered fast during opening hours.
          </p>
        </div>
      </section>

      <section className="py-20 bg-slate-50" data-testid="contact-grid">
        <div className="container-x grid lg:grid-cols-12 gap-10">
          {/* Form */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-slate-200 rounded-sm p-8 md:p-10 shadow-md">
              <h2 className="font-heading font-black text-3xl tracking-tighter text-brand-navy">
                Send us a message
              </h2>
              <p className="text-slate-600 mt-2">We typically reply within 30 minutes during opening hours.</p>

              {sent ? (
                <div className="mt-8 p-6 border border-green-200 bg-green-50 rounded-sm" data-testid="contact-success">
                  <p className="font-bold text-green-800">Thanks — your message is in.</p>
                  <p className="text-sm text-green-700 mt-1">
                    One of our team will be in touch shortly. For urgent matters please call{" "}
                    <a href={`tel:${COMPANY.phoneTel}`} className="font-bold underline">
                      {COMPANY.phone}
                    </a>
                    .
                  </p>
                  <button
                    onClick={() => setSent(false)}
                    className="btn-outline-navy mt-5"
                    data-testid="contact-send-another"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="overline mb-2 block">Name</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      required
                      data-testid="contact-name-input"
                      className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange font-semibold"
                    />
                  </div>
                  <div>
                    <label className="overline mb-2 block">Email</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      required
                      data-testid="contact-email-input"
                      className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange font-semibold"
                    />
                  </div>
                  <div>
                    <label className="overline mb-2 block">Phone</label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      data-testid="contact-phone-input"
                      className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange font-semibold"
                    />
                  </div>
                  <div>
                    <label className="overline mb-2 block">Subject</label>
                    <input
                      type="text"
                      value={form.subject}
                      onChange={(e) => update("subject", e.target.value)}
                      data-testid="contact-subject-input"
                      placeholder="e.g. CP12 enquiry"
                      className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange font-semibold"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="overline mb-2 block">Message</label>
                    <textarea
                      rows={5}
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      required
                      data-testid="contact-message-input"
                      className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange font-medium"
                    />
                  </div>
                  <div className="sm:col-span-2 flex justify-end">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="contact-submit-button"
                    >
                      {submitting ? "Sending…" : "Send message"} <Send className="w-4 h-4" />
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-5 space-y-6" data-testid="contact-sidebar">
            <div className="bg-brand-navy text-white p-8 rounded-sm">
              <h3 className="overline text-brand-orange">Direct lines</h3>
              <ul className="mt-5 space-y-5">
                <li className="flex items-start gap-4">
                  <Phone className="w-6 h-6 text-brand-orange mt-1 shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-300">Phone &amp; emergency line</p>
                    <a href={`tel:${COMPANY.phoneTel}`} className="font-heading font-bold text-2xl hover:text-brand-orange transition-colors" data-testid="contact-sidebar-phone">
                      {COMPANY.phone}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Mail className="w-6 h-6 text-brand-orange mt-1 shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-300">Email</p>
                    <a href={`mailto:${COMPANY.email}`} className="font-bold hover:text-brand-orange transition-colors">
                      {COMPANY.email}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <MapPin className="w-6 h-6 text-brand-orange mt-1 shrink-0" />
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-300">Office &amp; dispatch</p>
                    <p className="font-bold">{COMPANY.address}</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-white border border-slate-200 rounded-sm p-7 shadow-sm">
              <h3 className="overline inline-flex items-center gap-2">
                <Clock className="w-4 h-4" /> Operational hours
              </h3>
              <ul className="mt-4 divide-y divide-slate-200">
                {COMPANY.hours.map((h) => (
                  <li key={h.day} className="flex items-center justify-between py-3 text-sm">
                    <span className="font-bold text-brand-navy">{h.day}</span>
                    <span className={`font-semibold ${h.time === "Closed" ? "text-slate-400" : "text-slate-800"}`}>
                      {h.time}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-brand-orange text-white p-7 rounded-sm">
              <ShieldCheck className="w-8 h-8 mb-3" />
              <p className="font-heading font-bold text-xl">Gas Safe Registered</p>
              <p className="text-sm text-white/90 mt-1">No. {COMPANY.gasSafeNumber} · Verified annually</p>
            </div>
          </aside>
        </div>
      </section>

      {/* Map placeholder */}
      <section className="pb-20 bg-slate-50" data-testid="contact-map">
        <div className="container-x">
          <div className="rounded-sm overflow-hidden border border-slate-200 shadow-md aspect-[16/7] bg-slate-200">
            <iframe
              title="EL-Gas Plumbing on Google Maps"
              src="https://www.google.com/maps?q=Median+Rd,+Lower+Clapton,+London+E5+0PL&output=embed"
              className="w-full h-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </>
  );
}
